import { TriangleAlert } from 'lucide-react';

import React, { useMemo } from 'react';

import type { ImportReviewState, SplitwiseFile } from '@splitfiesta/core';
import {
  MAX_GROUP_NAME_LENGTH,
  MAX_IMPORT_MEMBERS,
  MAX_MEMBER_NAME_LENGTH,
  assignAllZeroRows,
  assignZeroRow,
  convertedTotalsPreview,
  currencySpend,
  distinctCategories,
  formatCurrency,
  formatDate,
  importableRows,
  ratePairs,
  reviewBlockers,
  zeroEffectRows,
  zeroRowsBulkChoice,
} from '@splitfiesta/core';
import { CURRENCIES, useNamespace, useTranslation } from '@splitfiesta/i18n';
import { GROUP_CATEGORIES, type GroupCategory } from '@splitfiesta/shared';
import { Alert, Button, Input } from '@splitfiesta/ui';

import { ImportCategoryOverrides } from '@/components/import/ImportCategoryOverrides';
import { ActionButtonGroup } from '@/components/ui/ActionButtonGroup';
import { SearchableCurrencySelect } from '@/components/ui/SearchableCurrencySelect';

import { useExchangeRatesQuery } from '@/hooks/queries/useExchangeRateQuery';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { getInputClasses } from '@/lib/styles/form';
import { cn } from '@/lib/utils';

import { ChoicePill } from './ui/ChoicePill';
import { CurrencySpendRow } from './ui/CurrencySpendRow';
import { ImportCard } from './ui/ImportCard';
import { ImportSection } from './ui/ImportSection';
import { MemberRow } from './ui/MemberRow';

interface ImportReviewStepProps {
  file: SplitwiseFile;
  review: ImportReviewState;
  /** The file's own currency is unsupported, so the device one stands in. */
  currencyFallback?: boolean;
  onChange: (next: ImportReviewState) => void;
  onBack: () => void;
  onContinue: () => void;
}

export function ImportReviewStep({
  file,
  review,
  currencyFallback = false,
  onChange,
  onBack,
  onContinue,
}: ImportReviewStepProps) {
  const { t, i18n } = useTranslation();
  useNamespace('import');
  useNamespace('expenses');
  useNamespace('groups');

  const { isOnline } = useNetworkStatus();

  const categories = useMemo(() => distinctCategories(file), [file]);

  // The totals describe what will be posted, so an assigned zero row counts,
  // a row that will be left out does not, and a flipped label previews as the
  // kind it imports as.
  const rows = useMemo(
    () =>
      importableRows(file, review.zeroRowAssignments, review.categoryOverrides),
    [file, review.zeroRowAssignments, review.categoryOverrides]
  );
  const spend = useMemo(() => currencySpend(rows), [rows]);
  // One rate per row date, the way the server converts them, so the preview
  // is the figure the import will actually store.
  const pairs = useMemo(
    () => ratePairs(rows, review.groupCurrency),
    [rows, review.groupCurrency]
  );
  const rateFor = useExchangeRatesQuery({
    pairs,
    to: review.groupCurrency,
    // A single-currency file has nothing to convert, and the totals section
    // it would sit under is only rendered for a mixed one.
    enabled: isOnline && spend.length > 1,
  });
  const convertedTotals = useMemo(
    () => convertedTotalsPreview(rows, review.groupCurrency, rateFor),
    [rows, review.groupCurrency, rateFor]
  );
  const hasConverted = convertedTotals.some(total => total.converted !== null);
  // `file.currencies` is most-frequent-first, so its head is the currency the
  // group would have kept. The count can still be zero when every row that
  // spoke it is one the import leaves out, and then there is nothing to say.
  const fileCurrency = file.currencies[0];
  const convertedRows = useMemo(
    () => rows.filter(row => row.currency !== review.groupCurrency).length,
    [rows, review.groupCurrency]
  );
  const showConvertingFrom =
    review.groupCurrency !== fileCurrency && convertedRows > 0;
  const blockers = reviewBlockers(review, file);
  const zeroRows = useMemo(() => zeroEffectRows(file), [file]);
  const hasSkippedRows = zeroRows.length > 0;
  const bulkChoice = zeroRowsBulkChoice(review, zeroRows);

  const renameMember = (column: number, name: string) =>
    onChange({
      ...review,
      memberNames: review.memberNames.map((current, index) =>
        index === column ? name : current
      ),
    });

  return (
    <div className="space-y-6">
      <ImportSection title={t('import:members.title')}>
        <ImportCard padded>
          <Input
            id="import-review-group-name"
            data-testid="import-review-group-name"
            label={t('import:members.groupNameLabel')}
            value={review.groupName}
            maxLength={MAX_GROUP_NAME_LENGTH}
            onChange={event =>
              onChange({ ...review, groupName: event.target.value })
            }
          />

          <div className="space-y-1">
            <label
              htmlFor="import-review-category"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('groups:fields.category')}
            </label>
            <select
              id="import-review-category"
              data-testid="import-review-category"
              value={review.groupCategory}
              onChange={event =>
                onChange({
                  ...review,
                  groupCategory: event.target.value as GroupCategory,
                })
              }
              className={getInputClasses()}
            >
              {GROUP_CATEGORIES.map(category => (
                <option key={category} value={category}>
                  {t(`groups:categories.${category}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="import-review-currency"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('import:members.currencyLabel')}
            </label>
            {/* `expense` mode rather than `group`: an import always needs a
                currency, and group mode offers an empty "none" option. */}
            <SearchableCurrencySelect
              id="import-review-currency"
              data-testid="import-review-currency"
              mode="expense"
              value={review.groupCurrency}
              onChange={value => onChange({ ...review, groupCurrency: value })}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('import:members.currencyHint')}
            </p>
            {showConvertingFrom && (
              <p
                data-testid="import-review-converting-from"
                className="text-xs text-gray-500 dark:text-gray-400"
              >
                {t('import:members.convertingFrom', {
                  count: convertedRows,
                  from: fileCurrency,
                  to: review.groupCurrency,
                })}
              </p>
            )}
            {currencyFallback && (
              <Alert
                type="info"
                data-testid="import-review-currency-fallback"
                message={t('import:members.currencyUnsupported', {
                  currency: review.groupCurrency,
                })}
              />
            )}
          </div>
        </ImportCard>
      </ImportSection>

      {spend.length > 1 && (
        // Only worth the space on a mixed-currency file: what the rows spend
        // in each currency, with the import's own conversion underneath once
        // every row's rate is known.
        <ImportSection
          title={t('import:summary.currencies')}
          hint={hasConverted ? t('import:members.convertedHint') : undefined}
        >
          <ImportCard rows data-testid="import-review-totals">
            {spend.map((entry, index) => {
              const converted = convertedTotals[index]?.converted ?? null;
              return (
                <CurrencySpendRow
                  key={entry.currency}
                  testId={`import-review-spend-${entry.currency}`}
                  code={entry.currency}
                  symbol={
                    CURRENCIES[entry.currency]?.symbolNative ?? entry.currency
                  }
                  rowsLabel={t('import:summary.rows', { count: entry.rows })}
                  amount={formatCurrency(entry.amount, entry.currency)}
                  isGroupCurrency={entry.currency === review.groupCurrency}
                  groupCurrencyLabel={t('import:members.currencyLabel')}
                  convertedLabel={
                    converted === null
                      ? null
                      : t('import:members.convertedPreview', {
                          amount: formatCurrency(
                            converted,
                            review.groupCurrency
                          ),
                        })
                  }
                />
              );
            })}
          </ImportCard>
        </ImportSection>
      )}

      <ImportSection
        title={t('import:members.selfTitle')}
        hint={t('import:members.hint')}
      >
        {/* What still blocks the step, where most of it is fixed (the "me"
            choice here, the names below) rather than only next to the button. */}
        {blockers.length > 0 && (
          <ImportCard
            tone="warning"
            padded
            data-testid="import-review-blockers"
          >
            <div className="flex items-start gap-2">
              <TriangleAlert
                className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300"
                aria-hidden
              />
              <ul className="space-y-1 text-sm text-amber-800 dark:text-amber-200">
                {blockers.map(key => (
                  <li key={key}>
                    {t(
                      key,
                      key === 'import:errors.tooManyMembers'
                        ? { max: MAX_IMPORT_MEMBERS }
                        : undefined
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </ImportCard>
        )}
        <div
          role="radiogroup"
          aria-label={t('import:members.selfTitle')}
          className="flex flex-wrap gap-2"
        >
          {file.members.map((header, column) => (
            <ChoicePill
              key={`${column}-${header}`}
              testId={`import-review-self-${column}`}
              name="import-review-self"
              label={review.memberNames[column]?.trim() || header}
              selected={review.selfColumn === column}
              onSelect={() => onChange({ ...review, selfColumn: column })}
            />
          ))}
        </div>
      </ImportSection>

      {/* Its own section, so the name fields read as optional edits rather
          than as the place to pick yourself. */}
      <ImportSection
        title={t('import:members.namesTitle')}
        hint={t('import:members.namesHint')}
      >
        <ImportCard rows data-testid="import-review-members">
          {file.members.map((header, column) => (
            <MemberRow
              key={`${column}-${header}`}
              inputId={`import-review-name-${column}`}
              inputTestId={`import-review-name-${column}`}
              header={header}
              name={review.memberNames[column] ?? ''}
              maxLength={MAX_MEMBER_NAME_LENGTH}
              nameLabel={t('import:members.nameLabel')}
              meLabel={t('import:members.me')}
              isSelf={review.selfColumn === column}
              onChangeName={name => renameMember(column, name)}
            />
          ))}
        </ImportCard>
      </ImportSection>

      <ImportSection
        title={t('import:categories.title')}
        hint={t('import:categories.hint')}
      >
        <ImportCard>
          <ImportCategoryOverrides
            categories={categories}
            overrides={review.categoryOverrides}
            onChange={(key, path) =>
              onChange({
                ...review,
                categoryOverrides: { ...review.categoryOverrides, [key]: path },
              })
            }
          />
        </ImportCard>
      </ImportSection>

      {hasSkippedRows && (
        <ImportSection
          title={t('import:skipped.title')}
          hint={t('import:skipped.hint')}
        >
          <div className="space-y-1">
            <label
              htmlFor="import-review-assign-zero"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('import:skipped.assignTo')}
            </label>
            <select
              id="import-review-assign-zero"
              data-testid="import-review-assign-zero"
              value={bulkChoice === null ? '' : String(bulkChoice)}
              onChange={event =>
                onChange(
                  assignAllZeroRows(
                    review,
                    file,
                    event.target.value === ''
                      ? null
                      : Number(event.target.value)
                  )
                )
              }
              className={cn(getInputClasses(), 'w-auto')}
            >
              <option value="">{t('import:skipped.leaveOut')}</option>
              {review.memberNames.map((name, column) => (
                <option key={column} value={String(column)}>
                  {name}
                </option>
              ))}
              {/* Only ever the displayed value of rows that disagree, so it is
                  offered as a state rather than as a choice. */}
              {bulkChoice === 'mixed' && (
                <option value="mixed" disabled>
                  {t('import:skipped.mixed')}
                </option>
              )}
            </select>
          </div>
          <ImportCard>
            <ul
              data-testid="import-review-zero-rows"
              className="divide-y divide-gray-100 text-sm dark:divide-gray-700"
            >
              {zeroRows.map(row => {
                const assigned = review.zeroRowAssignments[row.rowIndex];
                return (
                  <li
                    key={row.rowIndex}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                  >
                    <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-gray-100">
                      <span className="mr-2 text-gray-500 dark:text-gray-400">
                        {formatDate(row.date, i18n.language)}
                      </span>
                      {row.description}
                    </span>
                    <span className="shrink-0 font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(row.cost, row.currency)}
                    </span>
                    <select
                      data-testid={`import-review-zero-row-${row.rowIndex}`}
                      aria-label={t('import:skipped.rowAssign')}
                      value={assigned === undefined ? '' : String(assigned)}
                      onChange={event =>
                        onChange(
                          assignZeroRow(
                            review,
                            row.rowIndex,
                            event.target.value === ''
                              ? null
                              : Number(event.target.value)
                          )
                        )
                      }
                      className={cn(
                        getInputClasses(),
                        'w-full sm:w-auto sm:shrink-0'
                      )}
                    >
                      <option value="">
                        {t('import:skipped.leaveOutRow')}
                      </option>
                      {review.memberNames.map((name, column) => (
                        <option key={column} value={String(column)}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </li>
                );
              })}
            </ul>
          </ImportCard>
        </ImportSection>
      )}

      {/* The blockers card sits far above on a long file; say why the
          button is off right next to it. */}
      {blockers.length > 0 && (
        <p
          data-testid="import-review-fix-above"
          className="text-sm font-medium text-amber-800 dark:text-amber-200 sm:text-right"
        >
          {t('import:errors.fixAbove')}
        </p>
      )}

      <ActionButtonGroup>
        <Button
          variant="secondary"
          data-testid="import-review-back"
          onClick={onBack}
        >
          {t('import:confirm.back')}
        </Button>
        <Button
          variant="primary"
          data-testid="import-review-continue"
          disabled={blockers.length > 0}
          onClick={onContinue}
        >
          {t('import:summary.continue')}
        </Button>
      </ActionButtonGroup>
    </div>
  );
}
